import React, { ReactNode, useState } from 'react';

import {
  Paragraph,
  Link,
  RadioGroup,
  Radio,
  Stack,
  CheckboxGroup,
  Checkbox,
  ModalBody,
  ModalFooter,
  Button,
  useToast,
  rem,
} from '@vimeo/bokeh';
import { translate } from '@vimeo/i18n-runtime';

interface PostData {
  token: string;
  content_ratings?: number[];
  content_rating?: number;
}

const serializePrefs = (prefData: PostData) => {
  return Object.keys(prefData)
    .reduce((acc: string[], key: string) => {
      const value = prefData[key];

      if (Array.isArray(value)) {
        const keyedValues = value.map((arrayVal: number, index: number) => {
          const arrayKey = `${key}[${index}]`;
          return `${encodeURIComponent(arrayKey)}=${encodeURIComponent(String(arrayVal))}`;
        });

        return [...acc, ...keyedValues];
      }

      acc.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
      return acc;
    }, [])
    .join('&');
};

type Filters = Record<string, { rating: number; name: string }>;

interface Props {
  selected: number;
  filters: Filters;
  customFilters: Filters;
  token: string;
  onClose: () => void;
}

export function ContentViewingPrefsForm({
  filters,
  customFilters,
  token,
  selected,
  onClose,
}: Props) {
  const toast = useToast();

  const defaultCustomFilters = Object.values(customFilters).reduce((acc, curr) => {
    // perform bitwise AND operation to determine the default selected checkboxes
    if ((curr.rating & selected) === 0) {
      acc.add(curr.rating.toString());
    }
    return acc;
  }, new Set<string>());

  const isCustomFilterSelected =
    selected !== filters.everything.rating && selected !== filters.safest.rating;
  const defaultFilter = isCustomFilterSelected ? '' : selected.toString();

  const [selectedFilter, setSelectedFilter] = useState(defaultFilter);
  const [selectedCustomFilters, setSelectedCustomFilters] = useState(defaultCustomFilters);
  const [isSaving, setIsSaving] = useState(false);

  // Everything, nothing or custom
  const contentViewingFilterKeys = Object.keys(filters);

  // Custom options
  const customContentViewingFilterKeys = Object.keys(customFilters);

  // Basically is not everything or nothing.
  // Actual number value is calculated from selected custom options
  const hasCustomViewingPref =
    selectedFilter !== filters.everything.rating.toString() &&
    selectedFilter !== filters.safest.rating.toString();

  const onChangeFilter = (rating: string) => {
    setSelectedFilter(rating);
    const ratings: string[] = Object.values(customFilters).map((filter) =>
      filter.rating.toString(),
    );

    // The rating value of "Let me choose" is an empty string
    if (!rating) {
      // Default all custom options to checked if the user selects "Let me choose"
      setSelectedCustomFilters(new Set(ratings));
    }
  };

  const onSubmitContentViewingPreferences = () => {
    setIsSaving(true);

    const data: PostData = { token };
    if (hasCustomViewingPref) {
      data.content_ratings = Array.from(selectedCustomFilters).map((rating) => parseInt(rating));
    } else {
      data.content_rating = parseInt(selectedFilter);
    }

    fetch('/settings', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: serializePrefs(data),
    }).then((response) => {
      setIsSaving(false);

      if (!response.ok) {
        toast({
          title: translate('Sorry, something went wrong. Please try again.'),
          status: 'error',
        });
      } else {
        toast({
          title: translate('Your preferences were successfully updated.'),
          status: 'success',
        });
        onClose();
        window.location.reload();
      }
    });
  };

  return (
    <>
      <ModalBody>
        <Stack spacing={rem(10)}>
          <Paragraph size="md">
            {translate('Choose what types of videos you do and do not want to see on Vimeo.')}{' '}
            {translate({
              singular: 'Learn more about what constitutes mature content in our {A}FAQ{/A}.',
              replacements: {
                A: (children: ReactNode) => (
                  <Link
                    variant="inline-primary"
                    href="/help/sso?redirect_to=https://help.vimeo.com/hc/en-us/articles/12426157083537-Content-ratings"
                    isExternal
                    fontSize="md"
                  >
                    {children}
                  </Link>
                ),
              },
            })}
          </Paragraph>

          <RadioGroup
            onChange={(rating) => {
              onChangeFilter(rating);
            }}
            value={selectedFilter}
          >
            <Stack>
              {contentViewingFilterKeys.map((filterKey) => {
                const filter = filters[filterKey];
                return (
                  <Radio key={filter.name} value={filter.rating.toString()}>
                    {filter.name}
                  </Radio>
                );
              })}
            </Stack>
          </RadioGroup>

          {hasCustomViewingPref && (
            <CheckboxGroup>
              <Stack paddingLeft="md">
                {customContentViewingFilterKeys.map((customFilterKey) => {
                  const filter = customFilters[customFilterKey];
                  return (
                    <Checkbox
                      key={`${customFilterKey}-${filter.rating}`}
                      id={filter.rating.toString()}
                      isChecked={selectedCustomFilters.has(filter.rating.toString())}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        if (checked) {
                          const newCustomFilters = new Set(selectedCustomFilters);
                          newCustomFilters.add(filter.rating.toString());
                          setSelectedCustomFilters(newCustomFilters);
                        } else {
                          const newCustomFilters = new Set(selectedCustomFilters);
                          newCustomFilters.delete(filter.rating.toString());
                          setSelectedCustomFilters(newCustomFilters);
                        }
                      }}
                    >
                      {filter.name}
                    </Checkbox>
                  );
                })}
              </Stack>
            </CheckboxGroup>
          )}
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button variant="primary" onClick={onSubmitContentViewingPreferences} isLoading={isSaving}>
          {translate('Apply changes')}
        </Button>
      </ModalFooter>
    </>
  );
}
